/**
 * Tineco PDP — behaviour for the "product.tineco-pdp" template sections.
 *
 * Everything lives under the window.TinecoPDP namespace and only touches
 * elements carrying data-tineco-* attributes inside .tineco-pdp sections,
 * so it cannot interfere with the rest of the theme.
 *
 * Features:
 *  - Scroll reveal (data-tineco-reveal, staggered via data-tineco-reveal-order)
 *  - In-view autoplay/pause for ambient looping videos (data-tineco-autoplay)
 *  - Count-up numbers (data-tineco-counter)
 *  - Video modal (data-tineco-modal-open / [data-tineco-modal])
 *  - Smooth scroll to the main buy box (data-tineco-scroll-to-buy)
 *  - Pinned scroll stages (data-tineco-pinned): title zoom-away reveal and
 *    scroll-driven image stacks (data-tineco-stack), like the chapter
 *    modules on global.tineco.com
 *  - Hover-to-play video groups (data-tineco-hover-group)
 *  - Smooth same-page anchors (a[data-tineco-smooth])
 *
 * The file may be requested by several sections on the same page; the guard
 * below makes re-execution a no-op. Theme editor reloads are handled through
 * the shopify:section:load event.
 */
(function () {
  'use strict';

  if (window.TinecoPDP) return;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------ */
  /* Scroll reveal                                                       */
  /* ------------------------------------------------------------------ */

  var revealObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('tineco-pdp-revealed');
        entry.target.classList.remove('tineco-pdp-pending');
        revealObserver.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
  );

  function initReveals(scope) {
    if (reducedMotion.matches) return;
    scope.querySelectorAll('[data-tineco-reveal]').forEach(function (el) {
      if (el.classList.contains('tineco-pdp-revealed') || el.classList.contains('tineco-pdp-pending')) return;
      var order = parseInt(el.getAttribute('data-tineco-reveal-order') || '0', 10);
      if (order > 0) el.style.setProperty('--tineco-reveal-delay', Math.min(order * 0.1, 0.8) + 's');
      el.classList.add('tineco-pdp-pending');
      revealObserver.observe(el);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Ambient video autoplay                                              */
  /* ------------------------------------------------------------------ */

  var videoObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        if (entry.isIntersecting && !reducedMotion.matches) {
          var playing = video.play();
          if (playing && playing.catch) playing.catch(function () {});
        } else if (!video.paused) {
          video.pause();
        }
      });
    },
    { rootMargin: '10% 0px 10% 0px' }
  );

  function initVideos(scope) {
    scope.querySelectorAll('video[data-tineco-autoplay]').forEach(function (video) {
      video.muted = true; // required for programmatic play on all browsers
      videoObserver.observe(video);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Count-up numbers                                                    */
  /* ------------------------------------------------------------------ */

  function animateCounter(el) {
    var raw = el.getAttribute('data-tineco-counter') || '0';
    var target = parseFloat(raw);
    if (isNaN(target)) return;

    var decimals = (raw.split('.')[1] || '').length;
    var duration = parseInt(el.getAttribute('data-tineco-counter-duration') || '1400', 10);
    var start = null;

    function step(timestamp) {
      if (start === null) start = timestamp;
      var progress = Math.min((timestamp - start) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      el.textContent = (target * eased).toFixed(decimals);
      if (progress < 1) requestAnimationFrame(step);
    }

    requestAnimationFrame(step);
  }

  var counterObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        counterObserver.unobserve(entry.target);
        if (reducedMotion.matches) {
          entry.target.textContent = entry.target.getAttribute('data-tineco-counter');
        } else {
          animateCounter(entry.target);
        }
      });
    },
    { threshold: 0.4 }
  );

  function initCounters(scope) {
    scope.querySelectorAll('[data-tineco-counter]').forEach(function (el) {
      if (el.hasAttribute('data-tineco-counter-bound')) return;
      el.setAttribute('data-tineco-counter-bound', '');
      el.textContent = '0';
      counterObserver.observe(el);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Video modal                                                         */
  /* ------------------------------------------------------------------ */

  function closeModal(modal) {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    modal.querySelectorAll('video').forEach(function (v) {
      v.pause();
    });
    // Stop embedded players by resetting their src.
    modal.querySelectorAll('iframe[data-tineco-src]').forEach(function (frame) {
      frame.src = '';
    });
    document.documentElement.style.removeProperty('overflow');
  }

  function openModal(modal) {
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    var frame = modal.querySelector('iframe[data-tineco-src]');
    if (frame && !frame.src) frame.src = frame.getAttribute('data-tineco-src');
    var video = modal.querySelector('video');
    if (video) {
      var playing = video.play();
      if (playing && playing.catch) playing.catch(function () {});
    }
    document.documentElement.style.overflow = 'hidden';
    var close = modal.querySelector('.tineco-pdp-modal__close');
    if (close) close.focus();
  }

  function initModals(scope) {
    scope.querySelectorAll('[data-tineco-modal-open]').forEach(function (trigger) {
      if (trigger.hasAttribute('data-tineco-modal-bound')) return;
      trigger.setAttribute('data-tineco-modal-bound', '');
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        var modal = document.getElementById(trigger.getAttribute('data-tineco-modal-open'));
        if (modal) openModal(modal);
      });
    });

    scope.querySelectorAll('[data-tineco-modal]').forEach(function (modal) {
      if (modal.hasAttribute('data-tineco-modal-bound')) return;
      modal.setAttribute('data-tineco-modal-bound', '');
      modal.addEventListener('click', function (event) {
        if (event.target === modal || event.target.closest('.tineco-pdp-modal__close')) {
          closeModal(modal);
        }
      });
    });
  }

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-tineco-modal].is-open').forEach(closeModal);
  });

  /* ------------------------------------------------------------------ */
  /* Pinned scroll stages (chapter title zoom + image stacks)            */
  /*                                                                     */
  /* A pinned element is a tall scroll track with a sticky 100vh stage.  */
  /* Scroll progress p runs 0→1 across the track:                        */
  /*  - default mode: past the trigger fraction the chapter title zooms  */
  /*    away (scale + fade, CSS transitions) and the content layer fades */
  /*    in — mirroring the "listting" modules on global.tineco.com.      */
  /*  - stack mode (data-tineco-stack): child images appear in sequence, */
  /*    either accumulating ("build") or one at a time ("crossfade").    */
  /* ------------------------------------------------------------------ */

  var pinnedActive = new Set();
  var pinnedFrame = null;

  function updatePinned(el) {
    var track = el.offsetHeight - window.innerHeight;
    var top = el.getBoundingClientRect().top;
    var progress = track > 0 ? Math.min(Math.max(-top / track, 0), 1) : 1;

    var stackMode = el.getAttribute('data-tineco-stack');
    if (stackMode) {
      var items = el.querySelectorAll('[data-tineco-stack-item]');
      if (!items.length) return;
      var index = Math.min(Math.floor(progress * items.length), items.length - 1);
      items.forEach(function (item, i) {
        var shown = stackMode === 'crossfade' ? i === index : i <= index;
        item.classList.toggle('is-shown', shown);
      });
    } else {
      var trigger = parseFloat(el.getAttribute('data-tineco-pinned-trigger')) || 0.35;
      el.classList.toggle('tineco-pdp-pinned--zoomed', progress >= trigger);
    }
  }

  function onPinnedScroll() {
    if (pinnedFrame) return;
    pinnedFrame = requestAnimationFrame(function () {
      pinnedFrame = null;
      pinnedActive.forEach(updatePinned);
    });
  }

  var pinnedObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          pinnedActive.add(entry.target);
          updatePinned(entry.target);
        } else {
          pinnedActive.delete(entry.target);
        }
      });
    },
    { rootMargin: '25% 0px 25% 0px' }
  );

  window.addEventListener('scroll', onPinnedScroll, { passive: true });
  window.addEventListener('resize', onPinnedScroll, { passive: true });

  function initPinned(scope) {
    scope.querySelectorAll('[data-tineco-pinned]').forEach(function (el) {
      if (el.hasAttribute('data-tineco-pinned-bound')) return;
      el.setAttribute('data-tineco-pinned-bound', '');
      // Reduced motion: never arm — the section stays a static, fully
      // visible 100vh block (see the CSS fallback state).
      if (reducedMotion.matches) return;
      el.classList.add('tineco-pdp-pinned--armed');
      pinnedObserver.observe(el);
      updatePinned(el);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Hover-to-play video groups                                          */
  /* ------------------------------------------------------------------ */

  function initHoverPlay(scope) {
    scope.querySelectorAll('[data-tineco-hover-group]').forEach(function (group) {
      if (group.hasAttribute('data-tineco-hover-bound')) return;
      group.setAttribute('data-tineco-hover-bound', '');
      var videos = Array.prototype.slice.call(group.querySelectorAll('video[data-tineco-hover-play]'));
      if (!videos.length) return;

      // Touch devices have no hover: fall back to in-view autoplay.
      if (window.matchMedia('(hover: none)').matches) {
        videos.forEach(function (video) {
          video.muted = true;
          videoObserver.observe(video);
        });
        return;
      }

      videos.forEach(function (video) {
        video.muted = true;
        var card = video.closest('[data-tineco-hover-card]') || video;
        card.addEventListener('mouseenter', function () {
          if (reducedMotion.matches) return;
          videos.forEach(function (other) {
            if (other !== video && !other.paused) other.pause();
          });
          var playing = video.play();
          if (playing && playing.catch) playing.catch(function () {});
        });
        card.addEventListener('mouseleave', function () {
          if (!video.paused) video.pause();
        });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Smooth same-page anchors (bento grid tiles)                         */
  /* ------------------------------------------------------------------ */

  function initSmoothAnchors(scope) {
    scope.querySelectorAll('a[data-tineco-smooth]').forEach(function (link) {
      if (link.hasAttribute('data-tineco-smooth-bound')) return;
      link.setAttribute('data-tineco-smooth-bound', '');
      link.addEventListener('click', function (event) {
        var href = link.getAttribute('href') || '';
        var hashIndex = href.indexOf('#');
        if (hashIndex === -1) return;
        var target;
        try {
          target = document.getElementById(decodeURIComponent(href.slice(hashIndex + 1)));
        } catch (error) {
          return;
        }
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Scroll to buy box                                                   */
  /* ------------------------------------------------------------------ */

  function initBuyLinks(scope) {
    scope.querySelectorAll('[data-tineco-scroll-to-buy]').forEach(function (trigger) {
      if (trigger.hasAttribute('data-tineco-buy-bound')) return;
      trigger.setAttribute('data-tineco-buy-bound', '');
      trigger.addEventListener('click', function (event) {
        var buyBox = document.querySelector('product-form, form[action*="/cart/add"]');
        if (!buyBox) return; // fall through to the trigger's href
        event.preventDefault();
        buyBox.scrollIntoView({
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
          block: 'center'
        });
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Bootstrap                                                           */
  /* ------------------------------------------------------------------ */

  function init(scope) {
    initReveals(scope);
    initVideos(scope);
    initCounters(scope);
    initModals(scope);
    initBuyLinks(scope);
    initPinned(scope);
    initHoverPlay(scope);
    initSmoothAnchors(scope);
  }

  /**
   * Recompute all pinned-stage states from the current scroll position.
   * Useful after programmatic scrolls or dynamic layout changes.
   */
  function refresh() {
    document.querySelectorAll('[data-tineco-pinned-bound]').forEach(updatePinned);
  }

  window.TinecoPDP = { init: init, refresh: refresh };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor: re-scan a section after it is (re)loaded.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
